import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Observable, Subject, debounceTime } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { DataverseActionsEnum, DataverseSubActionEnum } from 'src/app/modules/app-dataverse/models/dataverse-actions-enum';
import { Dto } from 'src/app/modules/app-dataverse/models/dto';
import { Field } from 'src/app/modules/app-dataverse/models/fields';
import { DataverseV2Service } from 'src/app/modules/app-dataverse/services/dataverse-v2.service';
import { DataverseApiService } from '../../../modules/app-dataverse/services/dataverse-api.service';
import { NavigationActionBase } from '../../navigation-action-base';
import { NavigationService } from '../../navigation.service';
import { DataverseViewEnum } from '../../../modules/app-dataverse/models/dataverse-view-enum';
import { EventBusService } from '../../../services/event-bus.service';
import { AppStorageService } from '../../../services/app-storage.service';
/**
 * Handles the dataverse actions navigation component
 */
@Component({
  selector: 'dataverse-actions-navigation-v2',
  templateUrl: './dataverse-actions-navigation-v2.component.html',
  styleUrls: ['./dataverse-actions-navigation-v2.component.css'],
})
export class DataverseActionsNavigationV2Component extends NavigationActionBase implements OnInit {
  /**
   * Change detector reference
   */
  _ref: ChangeDetectorRef;
  /**
   * Navigation service
   */
  _navigationService: NavigationService;
  /**
   * Search field opened flag
   */
  searchFieldOpened = false;
  /**
   * Selected dto
   */
  selectedDto: Dto = new Dto();
  /**
   * Show the delete DTO button?
   */
  showDeleteDTOButton = false;
  /**
   * Selected field
   */
  selectedField$: Observable<Field>;
  /**
   * Is bu selected flag
   */
  isBuSelected = false;
  /**
   * Is key user selected flag
   */
  isKeyUserSelected = false;
  /**
   * Hidden bu dev overview icons flag
   */
  hideBuDevOverviewIcons = false;
  /**
   * Selected sub action
   */
  selectedSubAction: DataverseSubActionEnum = DataverseSubActionEnum.None;
  /**
   * Display search flag
   */
  displaySearch = false;
  /**
   * Number of selected fields
   */
  numberOfSelectedFields = 0;
  /**
   * Dataverse readonly mode flag
   */
  dataverseReadOnlyMode: boolean;
  /**
   * Show preview changes flag
   */
  showPreviewChanges = false;
  /**
   * Current user
   */
  currentUser: any;
  /**
   * Search subject
   */
  private searchSubject = new Subject<string>();
  /**
   * Is create template in progress flag
   */
  isCreateTemplateInProgress = false;
  /**
   * Gets is dev overview action allowed
   */
  get isDevOverviewActionAllowed(): boolean {
    return this.isAdminUser || this.isDataverseDeveloper;
  }
  /**
   * Gets is bu overview action allowed
   */
  get isBuOverviewActionAllowed(): boolean {
    return this.isAdminUser || this.isDataverseConsultant;
  }
  /**
   * Gets is key user overview action allowed
   */
  get isKeyUserOverviewActionAllowed(): boolean {
    return this.isAdminUser || this.isBusinessAnalyst;
  }
  /**
   * Constructor
   * @param ref
   * @param navigationService
   * @param service
   * @param dataverseService
   * @param apiService
   */
  constructor(appStorage: AppStorageService,
    ref: ChangeDetectorRef,
    navigationService: NavigationService,
    protected dataverseService: DataverseV2Service,
    eventBus: EventBusService,
    private apiService: DataverseApiService) {
    super(appStorage, eventBus, ref, navigationService);
    this._ref = ref;
    this._navigationService = navigationService;
    this.dataverseReadOnlyMode = environment.dataverseReadOnlyMode;
    this.searchSubject
      .pipe(debounceTime(500))
      .subscribe((term) => {
        this.dataverseService.searchFields(term);
        this.ref.markForCheck();
      });
  }
  /**
   * NgOnInit
   */
  ngOnInit(): void {
    //call base class ngOnInit, it won't be called automagically!!
    super.ngOnInit();

    this.selectedField$ = this.dataverseService.getActiveField$();

    this.subscriptions.add(this.dataverseService.getActiveDto$().subscribe(dto => {
      setTimeout(() => {
        this.selectedDto = dto ?? new Dto();
        if (dto == null) {
          this.showDeleteDTOButton = false;
        }
        else {
          this.showDeleteDTOButton = true;
        }
        this.hideBuDevOverviewIcons = false;
        this.ref.markForCheck();
      });
    }));

    this.subscriptions.add(this.dataverseService.activeSubAction$.subscribe(data => {
      setTimeout(() => {
        this.selectedSubAction = data;
        this.ref.markForCheck();
      });
    }));

    this.subscriptions.add(this.dataverseService.getSelectedFields$().subscribe((data: any[]) => {
      setTimeout(() => {
        this.numberOfSelectedFields = (data || []).length;
        this.ref.markForCheck();
      });
    }));

    this.subscriptions.add(this.dataverseService.isCreateTemplateInProgres.subscribe(data => {
      this.isCreateTemplateInProgress = data;
    }));

    this.onEvents.push(
      this.eventBus.subscribeEvent("showPreviewChangesIcon", (value) => {
        this.showPreviewChanges = value;
      }));
    this.onEvents.push(
      this.eventBus.subscribeEvent("NavigationActionBase:UserRolesAcquired", (that) => {
        if (this === that) {
          this.userRolesAcquired();
        }
      }));
  }

  /**
  * User roles are acquired in base class (NavigationActionBase)
  */
  private userRolesAcquired(): void {
    // if user is Business Analyst (and not admin) => switch to KeyUserOverview
    if ((this.isBusinessAnalyst || this.isDataverseKeyUser) && !this.isAdminUser) {
      setTimeout(() => {
        this.keyUserOverview();
      })
    }
    if (this.isDataverseConsultant && !this.isAdminUser) {
      setTimeout(() => {
        this.buOverview();
      })
    }
    if (this.devOverview && !this.isAdminUser) {
      setTimeout(() => {
        this.devOverview();
      })
    }
  }

  /**
  * Retrieves dataverse sub
  */
  public get dataverseSubActions(): typeof DataverseSubActionEnum {
    return DataverseSubActionEnum;
  }

  /**
   * On basket icon click action
   */
  onBasketClick() {
    if (this.selectedSubAction == this.dataverseSubActions.FieldsBucket) {
      this.dataverseService.setActiveDataverseSubAction(DataverseSubActionEnum.None);
    }
    else {
      this.dataverseService.setActiveDataverseSubAction(DataverseSubActionEnum.FieldsBucket);
    }
  }

  /**
   * On delete field action
   */
  deleteField() {
    this.dataverseService.setActiveDataverseSubAction(DataverseSubActionEnum.Delete);
  }

  /**
 * On delete field action
 */
  deleteDTO() {
    this.dataverseService.setActiveDataverseSubAction(DataverseSubActionEnum.DeleteDTO);
  }

  /**
   * On add new field action
   */
  addNewField() {
    this.dataverseService.setActiveDataverseSubAction(DataverseSubActionEnum.NewField);
  }

  /**
   * On edit field action
   */
  edit() {
    this.dataverseService.setActiveDataverseSubAction(DataverseSubActionEnum.EditField);
  }

  /**
  * On edit dto attributes action
  */
  editDtoAttributes() {
    this.dataverseService.setActiveDataverseSubAction(DataverseSubActionEnum.EditDto);
  }

  /**
   * On display search input action
   */
  public displaySearchInput() {
    this.displaySearch = true;
  }

  /**
   * On close search action
   */
  public closeSearch() {
    this.dataverseService.clearSearch();
    this.displaySearch = false;
    this.ref.markForCheck();
  }

  /**
   * Search fields list by provided search term
   * @param term Search term
   */
  public searchFields(term: string): void {
    this.searchSubject.next(term);
  }

  /**
   * Displays dev DTO and fields overview
   */
  public devOverview() {
    this.cleanSelectedData();
    this.isBuSelected = false;
    this.dataverseService.setActiveDataverseAction(DataverseActionsEnum.DEVOverView);
    this.hideBuDevOverviewIcons = false;
    this.isKeyUserSelected = false;
    this.dataverseService.setActiveDataverseView(DataverseViewEnum.DevelopmentView);
  }

  /**
  * Displays consulting DTO and fields overview
  */
  public buOverview() {
    this.cleanSelectedData();
    this.isBuSelected = true;
    this.dataverseService.setActiveDataverseAction(DataverseActionsEnum.BUOverView);
    this.hideBuDevOverviewIcons = false;
    this.isKeyUserSelected = false;
    this.dataverseService.setActiveDataverseView(DataverseViewEnum.BusinessView);
  }
  /**
   * Key user overview
   */
  public keyUserOverview() {
    this.cleanSelectedData();
    this.isKeyUserSelected = true;
    this.isBuSelected = false;
    this.dataverseService.setActiveDataverseAction(DataverseActionsEnum.KeyUserView)
    this.dataverseService.setActiveDataverseView(DataverseViewEnum.KeyUserView);
  }

  /**
  * Displays creating new export template form or templates list
  */
  public exportTemplates() {
    if (this.selectedSubAction == this.dataverseSubActions.CreateExportTemplate) {
      this.dataverseService.updateExportTemplateModel(true);
      this.dataverseService.setActiveDataverseSubAction(DataverseSubActionEnum.CancelExport);
    }
    else {
      this.dataverseService.setActiveDataverseSubAction(DataverseSubActionEnum.CreateExportTemplate);
      this.dataverseService.setActiveDto(null);
      this.dataverseService.setIsCreateTemplateInProgress(true);
    }
  }

  /**
* Opens edit template dialog
*/
  public editTemplate() {
    this.dataverseService.setActiveDataverseSubAction(DataverseSubActionEnum.EditTemplate);
    this.eventBus.broadcastEvent("editTemplate");
  }

  /**
* Opens delete template confirm dialog
*/
  public deleteTemplate() {
    this.dataverseService.setActiveDataverseSubAction(DataverseSubActionEnum.DeleteTemplate);
    this.eventBus.broadcastEvent("deleteTemplate");
  }

  /**
* Publishes or unpublishes template
*/
  public publishUnpublishTemplate() {
    this.eventBus.broadcastEvent("publishUnpublishTemplate");
  }

  /**
  * Displays copy new export template form or templates list
  */
  public copyExportTemplate() {
    if (this.isKeyUserSelected) {
      this.dataverseService.changeExportTemplateStep(2);
      this.dataverseService.setActiveDataverseSubAction(DataverseSubActionEnum.CopyExportTemplate);
    }
  }

  /**
   * On create new DTO contract action (publish dto)
   */
  public newDto() {
    this.dataverseService.setActiveDataverseSubAction(DataverseSubActionEnum.None);
    this.dataverseService.setActiveDataverseAction(DataverseActionsEnum.NewDTOContract);
    this.hideBuDevOverviewIcons = true;
  }
  /**
   * Preview changes
   */
  public previewChanges() {
    this.dataverseService.getActiveField$().subscribe(data => {
      if (data) {
        this.dataverseService.setActiveDataverseSubAction(DataverseSubActionEnum.PreviewChanges);
      }
    });
  }
  /**
  * Displays field references
  */
  public displayFieldReferences() {
    this.dataverseService.setActiveDataverseSubAction(DataverseSubActionEnum.ReferenceFields);
  }

  /**
   * Resets active dto and fields
   */
  private cleanSelectedData() {
    this.dataverseService.setActiveDataverseSubAction(DataverseSubActionEnum.None);
    this.dataverseService.setActiveDto(null);
    this.dataverseService.setActiveField(null);
  }

  /**
   * Exports DTO fields
   */
  protected export() {
    const boName = this.dataverseService.activeDto.name;
    this.apiService.export(this.dataverseService.filterSelectedFieldsForExport(boName), boName);
  }
}
